import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { VehicleCard } from '@/components/vehicles/VehicleCard';
import { getVehicles, getVehicleFilters } from '@/lib/api';
import { SEO, localBusinessSchema } from '@/components/SEO';
import { Search, Shield, CreditCard, Wrench, Truck, ChevronRight, Star, MapPin, Clock } from 'lucide-react';

export default function HomePage() {
    const navigate = useNavigate();
    const [featured, setFeatured] = useState([]);
    const [filters, setFilters] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchBrand, setSearchBrand] = useState('');
    const [searchPriceMax, setSearchPriceMax] = useState('');

    useEffect(() => {
        Promise.all([
            getVehicles({ limit: 6, featured_only: true }).catch(() => ({ data: { vehicles: [] } })),
            getVehicleFilters().catch(() => ({ data: {} }))
        ]).then(([vehiclesRes, filtersRes]) => {
            let vehicles = vehiclesRes.data.vehicles || [];
            if (vehicles.length === 0) {
                getVehicles({ limit: 6, sort: 'newest' })
                    .then(res => setFeatured(res.data.vehicles || []))
                    .catch(() => {});
            } else {
                setFeatured(vehicles);
            }
            setFilters(filtersRes.data);
        }).finally(() => setLoading(false));
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        const params = new URLSearchParams();
        if (searchBrand) params.set('brand', searchBrand);
        if (searchPriceMax) params.set('price_max', searchPriceMax);
        navigate(`/coches?${params.toString()}`);
    };

    const benefits = [
        { icon: Shield, title: 'Garantía propia', desc: 'Todos nuestros vehículos con garantía propia.' },
        { icon: CreditCard, title: 'Financiación a medida', desc: 'Te ayudamos a encontrar la mejor cuota.' },
        { icon: Wrench, title: 'Taller propio', desc: 'Cada coche pasa por nuestro taller antes de la venta.' },
        { icon: Truck, title: 'Entrega a domicilio', desc: 'Te lo llevamos donde estés.' },
    ];

    return (
        <div data-testid="home-page">
            <SEO
                title="Coches de Segunda Mano en Don Benito"
                description="ASG Motor - Concesionario de vehículos de ocasión en Don Benito, Badajoz. Coches revisados con garantía propia, financiación a medida y taller propio. Contacta por WhatsApp."
                path="/"
                schema={localBusinessSchema}
            />
            {/* Hero Section with dealership photo background */}
            <section className="relative overflow-hidden py-20 sm:py-28 lg:py-32" data-testid="hero-section">
                {/* Background image */}
                <div className="absolute inset-0">
                    <img src="/images/hero-asg-motor.jpg" alt="" className="w-full h-full object-cover" loading="eager" />
                    <div className="absolute inset-0 bg-black/55" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-black/10" />
                </div>
                <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8">
                        <h1 className="font-[var(--font-display)] tracking-tight text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg" data-testid="hero-title">
                            Tu próximo coche,<br />sin complicaciones.
                        </h1>
                        <p className="text-base md:text-lg text-white/80 max-w-xl mx-auto drop-shadow">
                            Vehículos revisados, historial transparente y atención inmediata por WhatsApp.
                        </p>
                    </div>

                    {/* Search Card */}
                    <Card className="relative max-w-2xl mx-auto rounded-2xl border-0 bg-white/95 backdrop-blur-md p-4 sm:p-5 shadow-2xl" data-testid="hero-search-card">
                        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
                            <div className="flex-1">
                                <Select value={searchBrand} onValueChange={setSearchBrand}>
                                    <SelectTrigger data-testid="hero-brand-select" className="h-11">
                                        <SelectValue placeholder="Marca" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas las marcas</SelectItem>
                                        {(filters?.brands || []).map(b => (
                                            <SelectItem key={b} value={b}>{b}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex-1">
                                <Input
                                    type="number"
                                    placeholder="Precio máximo €"
                                    value={searchPriceMax}
                                    onChange={(e) => setSearchPriceMax(e.target.value)}
                                    className="h-11"
                                    data-testid="hero-price-input"
                                />
                            </div>
                            <Button type="submit" className="h-11 px-6 bg-[var(--brand-navy)] hover:bg-[#0d3560] text-white" data-testid="hero-search-submit-button">
                                <Search className="h-4 w-4 mr-2" />
                                Buscar
                            </Button>
                        </form>
                    </Card>

                    {/* Trust chips */}
                    <div className="flex flex-wrap justify-center gap-2 mt-6">
                        {['Garantía propia', 'Taller propio', 'Financiación a medida'].map(chip => (
                            <span key={chip} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm border border-white/25 text-xs font-medium text-white">
                                <Star className="h-3 w-3 text-white/80" />
                                {chip}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Section */}
            <section className="py-10 sm:py-14" data-testid="home-featured-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="font-[var(--font-display)] tracking-tight text-2xl sm:text-3xl font-semibold text-[hsl(var(--foreground))]">
                            Vehículos destacados
                        </h2>
                        <Button variant="ghost" onClick={() => navigate('/coches')} className="text-[var(--brand-blue)] hover:text-[var(--brand-navy)]" data-testid="home-view-all-inventory-button">
                            Ver todo <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    </div>

                    {loading ? (
                        <div className="grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                            {[1,2,3].map(i => (
                                <Card key={i} className="overflow-hidden rounded-xl">
                                    <Skeleton className="aspect-[16/10] w-full" />
                                    <div className="p-4 space-y-3">
                                        <Skeleton className="h-4 w-3/4" />
                                        <Skeleton className="h-6 w-1/3" />
                                        <Skeleton className="h-3 w-full" />
                                    </div>
                                </Card>
                            ))}
                        </div>
                    ) : featured.length > 0 ? (
                        <div className="grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                            {featured.map(v => <VehicleCard key={v.id} vehicle={v} />)}
                        </div>
                    ) : (
                        <Card className="p-8 text-center">
                            <p className="text-[hsl(var(--muted-foreground))] mb-4">Aún no hay vehículos disponibles.</p>
                            <p className="text-sm text-[hsl(var(--muted-foreground))]">Vuelve pronto o contacta con nosotros.</p>
                        </Card>
                    )}
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-10 sm:py-14 bg-[var(--brand-gray-soft)]">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <h2 className="font-[var(--font-display)] tracking-tight text-2xl sm:text-3xl font-semibold text-center mb-8">
                        ¿Por qué ASG Motor?
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {benefits.map((b, i) => (
                            <Card key={i} className="p-5 rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--brand-light-blue)] mb-3">
                                    <b.icon className="h-5 w-5 text-[var(--brand-blue)]" />
                                </div>
                                <h3 className="font-[var(--font-display)] font-semibold text-sm mb-1">{b.title}</h3>
                                <p className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">{b.desc}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Location Section */}
            <section className="py-10 sm:py-14">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-8 items-center">
                        <div>
                            <h2 className="font-[var(--font-display)] tracking-tight text-2xl sm:text-3xl font-semibold mb-4">
                                Visítanos en Don Benito
                            </h2>
                            <div className="space-y-3 text-sm text-[hsl(var(--muted-foreground))]">
                                <div className="flex items-start gap-2">
                                    <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[var(--brand-blue)]" />
                                    <span>Avenida de Córdoba 15, 06400 Don Benito, Badajoz</span>
                                </div>
                                <div className="flex items-start gap-2">
                                    <Clock className="h-4 w-4 mt-0.5 shrink-0 text-[var(--brand-blue)]" />
                                    <span>L-V: 9:00 - 14:00 / 16:30 - 20:00 | S: 10:00 - 14:00</span>
                                </div>
                            </div>
                            <div className="mt-5 flex gap-3">
                                <Button onClick={() => navigate('/contacto')} className="bg-[var(--brand-navy)] hover:bg-[#0d3560] text-white">
                                    Contactar
                                </Button>
                                <Button variant="outline" asChild>
                                    <a href="https://www.google.com/maps/dir/?api=1&destination=avenida+de+cordoba+15+06400+Don+Benito+Badajoz" target="_blank" rel="noopener noreferrer">
                                        Cómo llegar
                                    </a>
                                </Button>
                            </div>
                        </div>
                        <div className="rounded-xl overflow-hidden border shadow-sm aspect-video bg-[var(--brand-gray-soft)]">
                            <iframe
                                title="Ubicación ASG Motor"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3124.0!2d-5.861!3d38.955!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sAvda+de+C%C3%B3rdoba+15%2C+06400+Don+Benito%2C+Badajoz!5e0!3m2!1ses!2ses!4v1700000000000!5m2!1ses!2ses"
                                className="w-full h-full border-0"
                                allowFullScreen
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
